import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, RefreshCw } from "lucide-mui";
import { useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import FormattedDate from "../../components/FormattedDate.tsx";
import { keys } from "../../api/queryKeys.ts";
import { api, getErrorMessage } from "../../api/client.ts";
import { DeclarativeViews } from "../../components/micro-apps/MicroAppViews.tsx";
import { MAX_DOCUMENT_REMINTS, SandboxFrame } from "../../components/micro-apps/SandboxFrame.tsx";
import CenteredSpinner from "../../components/CenteredSpinner.tsx";
import EmptyState from "../../components/EmptyState.tsx";
import PageHeader from "../../components/PageHeader.tsx";

/**
 * Renders a published micro app: its declarative `views` with first-party
 * components, or — for an app that ships `client_code` — its own document in a
 * sandboxed frame on the credential-free origin.
 */

function MicroAppHostPage() {
  const { slug = "" } = useParams();

  // What the sandbox iframe currently holds. Telling the backend about it
  // suppresses a fresh single-use document on every poll — the frame is kept
  // and the new data pushed into it instead. Cleared implicitly on remount,
  // which is correct: a remounted frame does need its own document.
  const mounted = useRef<{ slug: string; version: number; url: string } | null>(null);
  const matchesScope = (m: typeof mounted.current) => !!m && m.slug === slug;

  const { data, error, isLoading, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: keys.microApp.render(slug),
    queryFn: () =>
      api.renderMicroApp(slug, matchesScope(mounted.current) ? mounted.current!.version : null),
    enabled: !!slug,
    // The manifest's own refresh interval, once we've seen it.
    refetchInterval: (query) => (query.state.data?.manifest.refresh_seconds ?? 300) * 1000,
    refetchOnWindowFocus: true,
    // Not cacheable across mounts: the response carries a single-use, 60s
    // document handle. Replaying one out of the cache points the iframe at a
    // handle the previous mount already consumed — and because the refetch
    // that follows sends `known_version`, the backend mints no replacement,
    // so the frame stays on the sandbox host's 404 until a full page reload.
    gcTime: 0,
  });

  // The frame never announced itself, so its document is gone — the handle was
  // already consumed, its 60s TTL lapsed, or the exec store missed. Forget what
  // we think is mounted and refetch: `known_version` is then omitted and the
  // backend mints a fresh document. The budget is per app, so navigating to
  // another one starts over.
  const remintsLeft = useRef(MAX_DOCUMENT_REMINTS);
  useEffect(() => {
    remintsLeft.current = MAX_DOCUMENT_REMINTS;
  }, [slug]);
  const handleDocumentLost = useCallback(() => {
    if (remintsLeft.current <= 0) return;
    remintsLeft.current -= 1;
    mounted.current = null;
    void refetch();
  }, [refetch]);

  const header = (
    <PageHeader
      title={data?.app.title ?? "App"}
      back={{ to: "/apps", label: "Apps" }}
      description={
        data && (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5 }}>
            <Chip size="small" label={`v${data.version}`} variant="outlined" />
            {data.app.description && <span>{data.app.description}</span>}
            <span>
              · Updated <FormattedDate value={dataUpdatedAt} format="time" />
            </span>
          </Stack>
        )
      }
      actions={
        <Tooltip title="Refresh">
          <span>
            <IconButton aria-label="Refresh" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw />
            </IconButton>
          </span>
        </Tooltip>
      }
    />
  );

  if (isLoading) {
    return (
      <Box>
        {header}
        <CenteredSpinner py={6} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        {header}
        <Alert severity="error">{getErrorMessage(error)}</Alert>
      </Box>
    );
  }
  if (!data) return header;

  const views = data.manifest.views ?? [];
  // An app with client_code draws itself in the sandbox; otherwise the
  // manifest's declarative views are rendered with first-party components.
  // A null document_url on a refresh means "keep the frame you have", so the
  // last URL is held rather than re-read from every response.
  if (data.document_url) {
    mounted.current = { slug, version: data.version, url: data.document_url };
  }
  const documentUrl = matchesScope(mounted.current) ? mounted.current!.url : null;

  return (
    <Box>
      {header}

      {data.server_error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This app&rsquo;s data transform failed, so the figures below are untransformed:{" "}
          {data.server_error}
        </Alert>
      ) : null}

      {documentUrl ? (
        <SandboxFrame
          src={documentUrl}
          title={data.app.title}
          slug={data.app.slug}
          version={data.version}
          fetchedAt={data.fetched_at}
          data={data.data}
          onDocumentLost={handleDocumentLost}
        />
      ) : data.has_client_code ? (
        // No document yet for an app that draws its own: the mount fetch, or
        // the remint after a lost document, is still in flight. Showing the
        // declarative empty state here would flash "no views" at a working app.
        <CenteredSpinner py={6} />
      ) : views.length === 0 ? (
        <EmptyState
          variant="panel"
          icon={<LayoutDashboard />}
          title="Nothing to show yet"
          description="This app has no views to display yet."
        />
      ) : (
        <DeclarativeViews views={views} data={data.data} />
      )}
    </Box>
  );
}

export default MicroAppHostPage;
